// src/components/dialogs/TokenExpiryDialog.tsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  LinearProgress,
  Box,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { Warning as WarningIcon, Timer as TimerIcon } from '@mui/icons-material';

interface TokenExpiryDialogProps {
  open: boolean;
  secondsRemaining: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

export const TokenExpiryDialog: React.FC<TokenExpiryDialogProps> = ({
  open,
  secondsRemaining,
  onExtendSession,
  onLogout,
}) => {
  const translate = useTranslate();

  // Formatear el tiempo restante
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Calcular el progreso (asumiendo 5 minutos = 300 segundos como máximo)
  const progress = Math.max(0, Math.min(100, (secondsRemaining / 300) * 100));

  // Determinar el color según el tiempo restante
  const getAlertSeverity = () => {
    if (secondsRemaining <= 30) return 'error';
    if (secondsRemaining <= 60) return 'warning';
    return 'info';
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        pb: 1 
      }}>
        <WarningIcon color="warning" />
        {translate('auth.session_expiry.title', 'Sesión a punto de expirar')}
      </DialogTitle>
      
      <DialogContent>
        <Alert 
          severity={getAlertSeverity()} 
          icon={<TimerIcon />}
          sx={{ mb: 2 }}
        >
          {translate(
            'auth.session_expiry.message',
            `Tu sesión expirará en ${formatTime(secondsRemaining)}. ¿Deseas extenderla?`
          ).replace('{time}', formatTime(secondsRemaining))}
        </Alert>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {translate('auth.session_expiry.time_remaining', 'Tiempo restante')}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: secondsRemaining <= 30 
                      ? 'error.main' 
                      : secondsRemaining <= 60 
                        ? 'warning.main' 
                        : 'primary.main',
                  }
                }}
              />
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                minWidth: 60,
                fontWeight: 'bold',
                color: secondsRemaining <= 30 ? 'error.main' : 'text.primary'
              }}
            >
              {formatTime(secondsRemaining)}
            </Typography>
          </Box>
        </Box>

        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mt: 2 }}
        >
          {translate(
            'auth.session_expiry.info',
            'Por seguridad, las sesiones expiran después de un período de inactividad. Puedes extender tu sesión o cerrar sesión ahora.'
          )}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onLogout}
          color="inherit"
          variant="outlined"
        >
          {translate('ra.auth.logout', 'Cerrar sesión')}
        </Button>
        
        <Button 
          onClick={onExtendSession}
          variant="contained"
          autoFocus
          sx={{ minWidth: 120 }}
        >
          {translate('auth.session_expiry.extend', 'Extender sesión')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenExpiryDialog;